import React from "react";
import { Image, Typography } from "antd";

const { Text } = Typography;

// Fallback for a `src` that antd's own Image tried to load and failed (a
// stale/expired blob URL) — kept as an inline SVG data URI so antd can use it
// directly as `fallback`, no extra asset/network request. This is the rarer,
// genuinely-broken case; the common "never took this optional photo" case
// below doesn't attempt a request at all, so it never reaches this path.
const PLACEHOLDER =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E" +
  "%3Crect width='100%25' height='100%25' fill='%23fafafa'/%3E" +
  "%3Ctext x='50%25' y='50%25' font-size='14' fill='%23bfbfbf' text-anchor='middle' dominant-baseline='middle'%3ENot available%3C/text%3E" +
  "%3C/svg%3E";

/**
 * Renders one "Label : <photo>" field, the shape almost every optional
 * sample/order photo on this page takes. Owning the label here (not just the
 * image) is what lets the empty case collapse onto a single line: a parent
 * laid out with flexDirection: column stacks whatever it's given as separate
 * rows regardless of what each child renders internally, so a compact
 * placeholder only reads as compact if the label and the placeholder are the
 * SAME child — hence this takes `label` itself now, rather than expecting a
 * caller to render a label span next to it.
 *
 * No `src` (an optional photo — seal/QR/selfie/etc. — that was simply never
 * taken, as on a backdated/historical order) renders "Label : N.A." inline,
 * no image request attempted. A `src` that fails to load still falls back to
 * the boxed placeholder via antd's own `fallback` — a real, if broken, photo
 * still deserves its own visual space, unlike "no photo at all".
 */
export default function SafeImage({ label, src, alt, width = 200, height = 200, style, ...rest }) {
  if (!src) {
    return (
      <span>
        {label && <Text strong>{label} :</Text>}{" "}
        <Text type="secondary">N.A.</Text>
      </span>
    );
  }

  return (
    <div>
      {label && <div style={{ marginBottom: 4 }}><Text strong>{label} :</Text></div>}
      <Image
        src={src}
        alt={alt}
        width={width}
        height={height}
        style={style}
        fallback={PLACEHOLDER}
        {...rest}
      />
    </div>
  );
}
